import { useEffect, useMemo, useRef, useState } from "react";
import type { SelectOption } from "../formData";

type Props = {
  id: string;
  label: string;
  placeholder: string;
  searchPlaceholder: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  emptyText?: string;
};

const normalize = (value: string) => value.normalize("NFKC").toLocaleLowerCase("en");

export default function SearchSelect({
  id,
  label,
  placeholder,
  searchPlaceholder,
  options,
  value,
  onChange,
  disabled = false,
  emptyText = "ရှာဖွေမှုနှင့် ကိုက်ညီသော အချက်အလက် မရှိပါ။"
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selected = options.find((option) => option.value === value);
  const filtered = useMemo(() => {
    const needle = normalize(query.trim());
    if (!needle) return options;
    return options.filter((option) => normalize(`${option.label} ${option.searchText ?? ""}`).includes(needle));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="search-select" ref={rootRef}>
      <span className="control-label" id={`${id}-label`}>{label}</span>
      <div className="search-select-row">
        <button
          type="button"
          className="search-select-button"
          role="combobox"
          aria-labelledby={`${id}-label`}
          aria-controls={`${id}-options`}
          aria-expanded={open}
          disabled={disabled}
          onClick={() => {
            setQuery("");
            setOpen((current) => !current);
          }}
        >
          <span className={selected ? "" : "placeholder"}>{selected?.label ?? placeholder}</span>
          <span aria-hidden="true">⌄</span>
        </button>
        {value && (
          <button type="button" className="clear-control" onClick={() => onChange("")} aria-label={`${label} ရွေးချယ်မှုကို ဖျက်ရန်`}>×</button>
        )}
      </div>
      {open && (
        <div className="search-panel">
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            autoComplete="off"
          />
          <div className="search-options" role="listbox" id={`${id}-options`} aria-labelledby={`${id}-label`}>
            {filtered.length ? filtered.map((option) => (
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                className={option.value === value ? "selected" : ""}
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                  setQuery("");
                }}
              >
                {option.label}
              </button>
            )) : <p className="empty-results">{emptyText}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
